import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { FLAG_DEFAULTS } from '@a7y/types';
import type { FeatureFlags, FeatureFlagDoc, FeatureFlagName } from '@a7y/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FEATURE_REGISTRY, defaultReviewBy, seasonEndDate } from '@a7y/types';
import type {
  ComingSoonDetail,
  FeatureOwner,
  FeatureRegistryEntry,
  FeatureSidebarBinding,
  FeatureStatusRecord,
  FeatureStatusValue,
  WaitingListNotifyResult,
} from '@a7y/types';

const FLAG_DESCRIPTIONS: Record<FeatureFlagName, string> = {
  V2: 'Gates all v2 functionality. When enabled, all users see v2 features globally.',
  MAINTENANCE_MODE: 'Instant kill switch — blocks user-facing operations without redeployment.',
  CHANGE_TRACKING:
    "Gates the 'Changes Since Last Audit' / DiffBand surface across every engine page and the overview hero. Default ON in prod (A7Y-1132); kill switch — flip to off if diff classifier regresses.",
  AUDIT_EMAIL_ACTION_BULLETS:
    'A7Y-3380 — adds <=2 quality-gated action bullets under each needs-work pillar in the audit digest emails. Default OFF; flip only after Yevhen signs off on the calibration shortlist.',
  KW_CORPUS_CANDIDATES:
    'Keyword engine reads candidate keywords from the keyword corpus first and runs live suggestion discovery only for seeds the corpus cannot answer (A7Y-4799). Turn on together with the engine-version bump in A7Y-4801.',
  WEEKLY_REPORT_EMAIL:
    'A7Y-5130 — one Monday email per app (one per account with 4+ apps) replaces the scheduled-audit report and the competitor digest. Default OFF; switch on only after Yevhen approves the rendered emails.',
};

export function FeatureFlagsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: api.getFeatureFlags,
  });

  const mutation = useMutation({
    mutationFn: ({ flagName, enabled }: { flagName: string; enabled: boolean }) =>
      api.updateFeatureFlag(flagName, {
        enabled,
        description: FLAG_DESCRIPTIONS[flagName as FeatureFlagName],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
    },
  });

  const flags: FeatureFlags = data?.flags ?? FLAG_DEFAULTS;
  const documents: FeatureFlagDoc[] = data?.documents ?? [];

  const getDoc = (name: FeatureFlagName): FeatureFlagDoc | undefined =>
    documents.find((d) => d.name === name);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading feature flags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-0">
      <PageHeader title="Feature Flags" subtitle="Toggle feature flags across the platform" />

      <div className="space-y-4">
        {(Object.keys(FLAG_DEFAULTS) as FeatureFlagName[]).map((name) => {
          const enabled = flags[name];
          const doc = getDoc(name);
          const isPending = mutation.isPending && mutation.variables?.flagName === name;

          return (
            <div
              key={name}
              className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{name}</h3>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-zinc-700 dark:text-zinc-300'
                      }`}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {FLAG_DESCRIPTIONS[name]}
                  </p>
                  {doc && (
                    <p className="mt-2 text-xs text-gray-400 dark:text-zinc-500">
                      Last updated by {doc.updatedBy} on {new Date(doc.updatedAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => mutation.mutate({ flagName: name, enabled: !enabled })}
                  disabled={isPending}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-zinc-600'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {mutation.isError && (
        <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-400">
          Failed to update flag. Please try again.
        </div>
      )}

      <FeatureStatusSection />
    </div>
  );
}

// ===========================================================================
// A7Y-3004: Feature status (Beta / New)
//
// Rows are driven by FEATURE_REGISTRY -- `label` and `description` come from
// the registry entry, not from a page-local map. That is the whole point of
// the registry: adding a feature is one edit in one file, rather than an edit
// here plus an edit there with `undefined` as the failure mode (which is what
// FLAG_DESCRIPTIONS above does, and why it is not extended).
// ===========================================================================

// The `Record<FeatureStatusValue, string>` annotation is what makes a new
// status value a compile error here rather than a blank option label.
const STATUS_LABELS: Record<FeatureStatusValue, string> = {
  coming_soon: 'Soon',
  beta: 'Beta',
  new: 'New',
  ga: 'None',
};

/** A7Y-3046: the Soon option is offered ONLY for a key whose registry entry
 *  carries a `coming_soon` sidebar binding — the API 400s on any other key,
 *  because a Soon feature with no binding would have no row to render and no
 *  route to reach. TypeScript cannot catch a stale literal here, so
 *  `FeatureFlagsPage.test.tsx` pins both halves. */
const SOON_STATUS_OPTIONS: FeatureStatusValue[] = ['coming_soon', 'beta', 'new', 'ga'];
const DEFAULT_STATUS_OPTIONS: FeatureStatusValue[] = ['beta', 'new', 'ga'];

type ComingSoonBinding = Extract<FeatureSidebarBinding, { kind: 'coming_soon' }>;

function comingSoonBinding(entry: FeatureRegistryEntry): ComingSoonBinding | null {
  return entry.sidebar?.kind === 'coming_soon' ? entry.sidebar : null;
}

function FeatureStatusSection() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: ['feature-status'],
    queryFn: api.getFeatureStatus,
  });

  // A7Y-3046: the waiting-list size per key. Its own query rather than a field
  // on the status record, because the count is an aggregation over a different
  // collection and changes without the status record changing.
  const { data: waiting } = useQuery({
    queryKey: ['feature-status-waiting'],
    queryFn: api.getFeatureStatusWaiting,
  });

  const mutation = useMutation({
    mutationFn: ({
      featureKey,
      ...body
    }: {
      featureKey: string;
      status: FeatureStatusValue;
      reviewBy?: string;
      knownGaps?: string[];
      owner?: FeatureOwner;
      comingSoon?: ComingSoonDetail;
    }) => api.updateFeatureStatus(featureKey, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-status'] });
      // A save that flipped a feature off Soon drains the waiting list, so the
      // count moves too.
      queryClient.invalidateQueries({ queryKey: ['feature-status-waiting'] });
    },
  });

  const records: Record<string, FeatureStatusRecord> = data?.records ?? {};

  return (
    <div className="mt-10">
      <PageHeader
        title="Feature status"
        subtitle="Label a feature Soon, Beta or New. Flipping a status takes effect on the next page load — no deploy."
      />

      {isError && (
        <div
          data-testid="feature-status-load-error"
          className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-400"
        >
          Failed to load feature status. Saving is disabled until the current status can be read —
          reload the page to try again.
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading feature status...</p>
      ) : (
        <div className="space-y-4">
          {FEATURE_REGISTRY.filter((entry) => !entry.orgOnly).map((entry) => (
            // Remount on the load/loaded transition so the row's snapshotted
            // form state re-seeds from the server record once the query
            // succeeds; without it a row that first rendered without data is
            // stuck on its "ga" default forever.
            <FeatureStatusRow
              key={`${entry.key}:${isSuccess ? 'loaded' : 'unloaded'}`}
              entry={entry}
              record={records[entry.key]}
              waitingCount={waiting?.counts?.[entry.key]}
              notification={
                mutation.isSuccess && mutation.variables?.featureKey === entry.key
                  ? mutation.data?.notification
                  : undefined
              }
              isPending={mutation.isPending && mutation.variables?.featureKey === entry.key}
              // Never let a Save POST a status derived from an unread record:
              // that overwrites live Beta state with the "ga" default.
              canSave={isSuccess}
              onSave={(body) => mutation.mutate({ featureKey: entry.key, ...body })}
            />
          ))}
        </div>
      )}

      {mutation.isError && (
        <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-400">
          Failed to update feature status. Please try again.
        </div>
      )}
    </div>
  );
}

function FeatureStatusRow({
  entry,
  record,
  waitingCount,
  notification,
  isPending,
  canSave,
  onSave,
}: {
  entry: FeatureRegistryEntry;
  record: FeatureStatusRecord | undefined;
  waitingCount: number | undefined;
  notification: WaitingListNotifyResult | undefined;
  isPending: boolean;
  canSave: boolean;
  onSave: (body: {
    status: FeatureStatusValue;
    reviewBy?: string;
    knownGaps?: string[];
    owner?: FeatureOwner;
    comingSoon?: ComingSoonDetail;
  }) => void;
}) {
  const [status, setStatus] = useState<FeatureStatusValue>(record?.status ?? 'ga');
  const [reviewBy, setReviewBy] = useState<string>(record?.reviewBy ?? '');
  const [knownGaps, setKnownGaps] = useState<string>((record?.knownGaps ?? []).join('\n'));
  const [owner, setOwner] = useState<FeatureOwner>(record?.owner ?? entry.defaultOwner);

  // A7Y-3046: Soon content. Admin-authored, server-validated as required.
  const [expected, setExpected] = useState<string>(record?.comingSoon?.expected ?? '');
  const [plan, setPlan] = useState<string>(record?.comingSoon?.plan ?? '');
  const [stores, setStores] = useState<string>(record?.comingSoon?.stores ?? '');
  const [teaserTitle, setTeaserTitle] = useState<string>(
    record?.comingSoon?.teaser.title ?? ''
  );
  const [teaserBody, setTeaserBody] = useState<string>(record?.comingSoon?.teaser.body ?? '');
  // Once the admin edits the date themselves, the season stops overwriting it.
  const [reviewByTouched, setReviewByTouched] = useState<boolean>(!!record?.reviewBy);

  const binding = comingSoonBinding(entry);
  const isSoon = status === 'coming_soon';

  const onStatusChange = (next: FeatureStatusValue) => {
    setStatus(next);
    // Pre-fill today+60 on the transition into Beta so the reminder job has a
    // date to fire on. The server applies the same default when the field is
    // left blank.
    if (next === 'beta' && !reviewBy) setReviewBy(defaultReviewBy(new Date()));
    // Soon has NO server-side default (D21): the season is plain words and a
    // guessed review date would silently become a real reminder. Suggest from
    // the season only when it parses and the field is untouched and blank.
    if (next === 'coming_soon') suggestReviewBy(expected);
  };

  const suggestReviewBy = (seasonText: string) => {
    if (reviewByTouched || reviewBy) return;
    const suggested = seasonEndDate(seasonText);
    if (suggested) setReviewBy(suggested);
  };

  const onExpectedChange = (next: string) => {
    setExpected(next);
    if (isSoon) suggestReviewBy(next);
  };

  // Every field the API requires for a Soon record. Save is disabled while any
  // is blank, so the 400 is unreachable from this UI.
  const soonIncomplete =
    isSoon &&
    (!expected.trim() || !teaserTitle.trim() || !teaserBody.trim() || !reviewBy);

  return (
    <div
      data-testid={`feature-status-row-${entry.key}`}
      className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{entry.label}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{entry.description}</p>
          {record && (
            <p className="mt-2 text-xs text-gray-400 dark:text-zinc-500">
              Last updated by {record.updatedBy} on {new Date(record.updatedAt).toLocaleString()}
            </p>
          )}

          {/* A7Y-3046 (D1): placement is compiled, not admin-editable. The
              section names literal JSX regions in the webapp sidebar and the
              icon names a member of a closed union resolved by a map there, so
              neither can be validated server-side — a free-text value would
              render an invisible row or a blank glyph. Shown read-only so it
              is still visible to the person choosing the status. */}
          {binding && (
            <p
              data-testid={`feature-status-placement-${entry.key}`}
              className="mt-2 text-xs text-gray-500 dark:text-zinc-400"
            >
              Placement: {binding.section} section · {binding.icon} icon (set in
              packages/types/src/feature-status.ts)
            </p>
          )}

          {typeof waitingCount === 'number' && (
            <p
              data-testid={`feature-status-waiting-${entry.key}`}
              className="mt-1 text-xs text-gray-500 dark:text-zinc-400"
            >
              Waiting list: {waitingCount}
            </p>
          )}

          {binding?.mechanismTest && (
            <p
              data-testid={`feature-status-mechanism-test-${entry.key}`}
              className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-300"
            >
              Mechanism-test key. Setting this to Soon shows a dimmed sidebar row and a teaser
              page to every signed-in user IN THIS ENVIRONMENT — environments do not share
              feature-status records. The API refuses it in production.
            </p>
          )}

          {/* Persistent until the list is fully drained: without it the only
              record of a half-sent waiting list is a Slack alert nobody can
              act on from here. Pressing Save sends the rest. */}
          {record?.pendingNotification && (
            <p
              data-testid={`feature-status-pending-notification-${entry.key}`}
              className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/20 p-2 text-xs text-amber-800 dark:text-amber-300"
            >
              Waiting-list email incomplete: {record.pendingNotification.unsent} of the list
              unsent for "{record.pendingNotification.event}" since{' '}
              {record.pendingNotification.since} ({record.pendingNotification.attempts}{' '}
              attempt(s)). Press Save to send the rest.
            </p>
          )}

          {notification && (
            <p
              data-testid={`feature-status-notification-result-${entry.key}`}
              className="mt-2 text-xs text-gray-600 dark:text-zinc-300"
            >
              Waiting-list email: {notification.sent} sent, {notification.failed} failed,{' '}
              {notification.remaining} remaining.
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Status
          <select
            aria-label={`Status for ${entry.label}`}
            value={status}
            onChange={(e) => onStatusChange(e.target.value as FeatureStatusValue)}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
          >
            {(binding ? SOON_STATUS_OPTIONS : DEFAULT_STATUS_OPTIONS).map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm text-gray-700 dark:text-gray-300">
          Owner
          <select
            aria-label={`Owner for ${entry.label}`}
            value={owner}
            onChange={(e) => setOwner(e.target.value as FeatureOwner)}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
          >
            <option value="yevhen">Yevhen</option>
            <option value="tpf">TPF</option>
          </select>
        </label>

        {/* Beta and Soon: the review-by date is what arms the daily
            graduation reminder. New has no review date -- its `newUntil` is
            computed server-side and is deliberately not editable here. For
            Soon the date is REQUIRED and admin-chosen (D21): the season is
            plain words, so the server never derives a date from it. */}
        {(status === 'beta' || isSoon) && (
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Review by
            <input
              type="date"
              aria-label={`Review by date for ${entry.label}`}
              value={reviewBy}
              onChange={(e) => {
                setReviewByTouched(true);
                setReviewBy(e.target.value);
              }}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
            />
          </label>
        )}

        {isSoon && (
          <>
            <label className="text-sm text-gray-700 dark:text-gray-300">
              Expected (season)
              <input
                type="text"
                aria-label={`Expected season for ${entry.label}`}
                value={expected}
                placeholder="Autumn 2026"
                onChange={(e) => onExpectedChange(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Plan
              <input
                type="text"
                aria-label={`Plan for ${entry.label}`}
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Stores
              <input
                type="text"
                aria-label={`Stores for ${entry.label}`}
                value={stores}
                onChange={(e) => setStores(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300">
              Teaser title
              <input
                type="text"
                aria-label={`Teaser title for ${entry.label}`}
                value={teaserTitle}
                onChange={(e) => setTeaserTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="text-sm text-gray-700 dark:text-gray-300 sm:col-span-2">
              Teaser body
              <textarea
                aria-label={`Teaser body for ${entry.label}`}
                value={teaserBody}
                rows={3}
                onChange={(e) => setTeaserBody(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
              />
            </label>
          </>
        )}

        <label className="text-sm text-gray-700 dark:text-gray-300 sm:col-span-2">
          Known gaps (one per line)
          <textarea
            aria-label={`Known gaps for ${entry.label}`}
            value={knownGaps}
            rows={3}
            onChange={(e) => setKnownGaps(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={isPending || !canSave || soonIncomplete}
          onClick={() =>
            onSave({
              status,
              ...((status === 'beta' || isSoon) && reviewBy ? { reviewBy } : {}),
              ...(isSoon
                ? {
                    comingSoon: {
                      expected: expected.trim(),
                      ...(plan.trim() ? { plan: plan.trim() } : {}),
                      ...(stores.trim() ? { stores: stores.trim() } : {}),
                      teaser: { title: teaserTitle.trim(), body: teaserBody.trim() },
                    },
                  }
                : {}),
              knownGaps: knownGaps
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
              owner,
            })
          }
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
